<script setup>
import { computed, provide, ref, watch } from 'vue'
import { useWidgetContext } from '@/__v1.5__/composables/useWidgetContext'
import CaptionHeader from '@/__v1.5__/components/caption-headers/CaptionHeader.vue'
import FallbackScreenHandler from '@/__core__/components/fallbackScreens/FallbackScreenHandler.vue'
import Branding from '@/__v1.5__/components/branding/Branding.vue'
import ScrollingContainer from '@/__core__/containers/scrolling-container/ScrollingContainer.vue'
import { useDataRefresh } from '@/__core__/data-refresh/useDataRefresh'
import useFetch from '@/__core__/composables/useFetch'
import { loc } from '@/__core__/localization/localization'
import { useFixturesStore } from '@/widgets/fixtures/Fixtures.store'
import MatchCard from '@/__v1.5__/components/cards/MatchCard.vue'
import EventPopup from '@/__v1.5__/components/popups/event-popup/EventPopup.vue'
import * as FixturesHelpers from './Fixtures.helpers'
import Message from '@/__v1.5__/components/message/Message.vue'
import '@/__core__/style/style.scss'

const SCROLL_OFFSET = 3 * (108 + 4)
const widgetId = 'fixtures'

const props = defineProps({
  languageIdentifier: {
    type: String,
    default: 'eng'
  },
  seasonId: {
    type: String,
    default: 'sr:season:118689'
  },
  startDate: {
    type: String,
    default: '2025-03-01'
  },
  endDate: {
    type: String,
    default: '2025-03-31'
  },
  round: [String, Number],
  roundIdentifier: String,
  sportType: {
    type: String,
    default: 'football'
  },
  theme: {
    type: String,
    default: 'light'
  },
  size: {
    type: Number,
    default: 640
  },
  themeConfig: {
    type: Object,
    default: () => null
  }
})

const containerRef = ref(null)
const activeGroupIndex = ref(0)
/** @type {Ref<HTMLDivElement>} */
const rowsContainerRef = ref(null)

const fixturesStore = useFixturesStore()
const { activeTheme, branded, containerClasses, langId, widgetHeight, widgetWidth, isAdaptiveHeight, buttonOffset, isLargeWidget } = useWidgetContext({
  ...props,
  containerRef,
  widgetId
})
const { startRefresh } = useDataRefresh(5 * 60 * 1000, widgetId)

const { status } = useFetch(async () => {
  const payload = {
    seasonId: props.seasonId,
    sportType: props.sportType,
    languageIdentifier: props.languageIdentifier,
    roundIdentifier: props.roundIdentifier,
    startDate: props.startDate,
    endDate: props.endDate,
    round: props.round
  }

  await fixturesStore.fetchFixturesData(payload)
  const drfHandlers = getDRFHandlers(payload)

  startRefresh(drfHandlers.refreshFn, drfHandlers.shouldCallApi, drfHandlers.shouldStopRefresh)
})

const containerBindings = computed(() => ({
  'e2e-id': 'szw-fixtures-widget',
  'theme-id': activeTheme.value,
  'lang-id': langId.value,
  'season-id': props.seasonId,
  'drf-id': widgetId
}))

const captionHeaderProps = computed(() => ({
  title: fixturesStore.competition.name,
  subtitle: `${loc('sm_season', langId.value)} ${fixturesStore.season.year}`,
  logoUrl: fixturesStore.competition?.logo?.light?.medium || '',
  disableTabs: true,
  hasShadow: true
}))

const containerId = computed(() => `rows-container-${activeGroupIndex.value}-large-${isLargeWidget.value}`)
const events = computed(() => (status.value !== 'loading' ? fixturesStore.events : Array.from({ length: 8 }).map((_, index) => ({ id: index }))))

const liveScoreboardProps = computed(() => ({
  languageIdentifier: langId,
  eventId: popupEvent.value?.id,
  sportType: props.sportType,
  theme: activeTheme.value,
  size: widgetWidth.value,
  themeConfig: props.themeConfig,
  timelineTypes: ['score_change']
}))

function getDRFHandlers(payload) {
  const refreshFn = async () => fixturesStore.scheduleFixturesRefresh(payload)
  const shouldCallApi = () => fixturesStore.getShouldCallApi()
  const shouldStopRefresh = () => fixturesStore.getShouldStopRefresh

  return { refreshFn, shouldCallApi, shouldStopRefresh }
}

const isEventPopupVisible = ref(false)
const popupEvent = ref(null)

function handleCloseEventPopup() {
  isEventPopupVisible.value = false
}

function handleMatchCardClick(event) {
  isEventPopupVisible.value = !isEventPopupVisible.value
  popupEvent.value = event
}

const getAdjustedEvent = FixturesHelpers.getAdjustedEvent(langId)

/* Scroll Animation */
const isScrollAnimationActive = ref(true)

function handleScrollReset() {
  isScrollAnimationActive.value = false

  const timeoutId = setTimeout(() => {
    isScrollAnimationActive.value = true
    clearTimeout(timeoutId)
  }, 300)
}

watch(isLargeWidget, handleScrollReset)

provide('status', status)
</script>

<template>
  <div :class="containerClasses" ref="containerRef" v-bind="containerBindings">
    <fallback-screen-handler>
      <branding v-if="branded" :brand="activeTheme" />

      <caption-header v-bind="captionHeaderProps" />
      <div class="szw-flex szw-flex-col szw-max-w-[768px] szw-w-full szw-flex-1 szw-mx-auto szw-gap-1">
        <scrolling-container
          v-if="!isAdaptiveHeight"
          :widget-container-height="widgetHeight"
          :container-ref="rowsContainerRef"
          :container-id="containerId"
          :button-offset="buttonOffset"
          disable-animation-when-reseting
          :override-scroll-offset="SCROLL_OFFSET"
        >
          <div :class="['szw-scrollable-container', { 'szw-transition-none': !isScrollAnimationActive }]" ref="rowsContainerRef">
            <div
              v-if="events?.length"
              class="szw-grid szw-w-full szw-gap-1"
              :style="{
                'grid-template-columns': !isLargeWidget ? '1fr' : '1fr 1fr'
              }"
            >
              <match-card v-for="event of events" :event="getAdjustedEvent(event)" :key="event.id" @card-clicked="handleMatchCardClick" />
            </div>

            <message v-else :message="loc('sm_refine_search', langId)" class="szw-mx-auto szw-mt-20" />
          </div>
        </scrolling-container>
      </div>

      <event-popup v-if="isEventPopupVisible" :liveScoreboardProps="liveScoreboardProps" :event="popupEvent" @popup-close="handleCloseEventPopup" />
    </fallback-screen-handler>
  </div>
</template>
